import type { Order } from '~api/tos';

import { Card, ListLayout } from '@ingrid/luterkort-react';

import { Amount } from '~src/components/Amount/Amount';
import { Dimensions } from '~src/components/Dimensions/Dimensions';
import { Weight } from '~src/components/Weight/Weight';
import { useTranslation } from '~src/i18n/use-translation';
import { Format } from '~src/utils/format';

type Props = Pick<Order, 'cart'>;

export function OrderDetails({ cart }: Props) {
  const { t } = useTranslation();

  if (!cart?.items) {
    return;
  }

  return (
    <Card.Wrapper>
      <Card.Header>{t('transportOrders.details.orderDetails')}</Card.Header>

      <Card.Content>
        <ListLayout.Column>
          {cart.items.map(({ currency, dimensions, name, price, quantity, weight }) => (
            <ListLayout.Row key={name}>
              <ListLayout.Stack>
                <ListLayout.SubRow>
                  <ListLayout.PrimaryText>{`${quantity ?? 1}×`}</ListLayout.PrimaryText>
                  <ListLayout.PrimaryText>{name}</ListLayout.PrimaryText>
                </ListLayout.SubRow>

                <ListLayout.SubRow gap="lg">
                  {dimensions && (
                    <ListLayout.SecondaryText>
                      <Dimensions dimensions={dimensions} />
                    </ListLayout.SecondaryText>
                  )}

                  {weight && (
                    <ListLayout.SecondaryText>
                      <Weight weight={weight} />
                    </ListLayout.SecondaryText>
                  )}

                  {currency && price && (
                    <ListLayout.SecondaryText>
                      <Amount currency={currency} value={price} />
                    </ListLayout.SecondaryText>
                  )}
                </ListLayout.SubRow>
              </ListLayout.Stack>
            </ListLayout.Row>
          ))}
        </ListLayout.Column>

        <ListLayout.Stack
          css={{
            borderBottomWidth: '0px',
            borderLeftWidth: '0px',
            borderRightWidth: '0px',
            borderStyle: 'solid',
            borderTopColor: 'var(--grey-e9)',
            borderTopWidth: '1px',
            marginTop: '1rem',
            paddingTop: '1rem',
          }}
        >
          <ListLayout.SubRow gap="lg">
            <ListLayout.SecondaryText>
              {t('transportOrders.details.item', { items: Format.TotalQuantity(cart?.items) })}
            </ListLayout.SecondaryText>

            <ListLayout.SecondaryText>
              <Weight weight={Format.TotalWeight(cart?.items)} />
            </ListLayout.SecondaryText>

            <ListLayout.SecondaryText>
              <Amount value={cart.total_value} currency={cart.currency} />
            </ListLayout.SecondaryText>
          </ListLayout.SubRow>
        </ListLayout.Stack>
      </Card.Content>
    </Card.Wrapper>
  );
}
